import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/context/AuthContext";
import { useCrumbs } from "@/context/CrumbContext";
import { Activity, ArrowLeft, Download, FileText, Filter, Search } from "lucide-react";

export const ACTION_LABELS = {
  "assessment.create": "Değerlendirme oluşturuldu",
  "finding.confirm": "Bulgu uygun bulundu",
  "finding.reject": "Bulgu uygunsuz işaretlendi",
  "finding.correct": "Bulgu düzeltme talep edildi",
  "finding.edit": "Aksiyon düzenlendi",
  "evidence.upload": "Kanıt yüklendi",
  "evidence.delete": "Kanıt silindi",
  "report.generate": "Rapor üretildi",
  "report.sign": "Rapor imzalandı",
  "report.archive": "Rapor arşivlendi olarak işaretlendi",
  "report.unarchive": "Rapor arşiv etiketi kaldırıldı",
  "report.share.create": "Paylaşım bağlantısı oluşturuldu",
  "report.share.revoke": "Paylaşım bağlantısı iptal edildi",
  "results.email": "Sonuç özeti e-postayla gönderildi",
  "summary.archive": "Sonuç özeti arşivlendi olarak işaretlendi",
  "summary.unarchive": "Sonuç özeti arşiv etiketi kaldırıldı",
  "member.invite": "Danışman davet edildi",
  "plan.ai_analyze": "Kroki AI ile analiz edildi",
};

export default function AuditLog() {
  const navigate = useNavigate();
  useCrumbs([{ label: "Denetim Günlüğü", to: "/audit" }]);
  const [rows, setRows] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/audit/log", { params: { limit: 500 } });
        setRows(data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const filtered = rows.filter((r) => {
    if (actionFilter && r.action !== actionFilter) return false;
    if (search && !(r.user_name||"").toLowerCase().includes(search.toLowerCase()) &&
        !(r.target_id||"").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const actions = Array.from(new Set(rows.map((r) => r.action)));

  const exportCsv = () => {
    const header = "created_at,action,user_name,user_email,target_type,target_id,assessment_id\n";
    const lines = filtered.map((r) => [
      r.created_at, r.action, r.user_name, r.user_email, r.target_type, r.target_id, r.assessment_id || "",
    ].map((x) => `"${String(x || "").replace(/"/g, "'")}"`).join(",")).join("\n");
    const blob = new Blob([header + lines], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="detail-shell" data-testid="audit-page">
      <header className="detail-topbar">
        <button className="icon-button" onClick={() => navigate("/home")} data-testid="audit-back"><ArrowLeft size={17}/></button>
        <div className="detail-head">
          <div className="eyebrow">DENETİM GÜNLÜĞÜ</div>
          <h1>Audit log</h1>
          <small><Activity size={13}/> {rows.length} kayıt · değiştirilemez tarihçe</small>
        </div>
        <div className="detail-actions">
          <button className="secondary-btn" onClick={exportCsv} data-testid="audit-export"><Download size={14}/> CSV dışa aktar</button>
        </div>
      </header>
      <div className="detail-body">
        <section className="section-block">
          <div className="section-title">
            <div><div className="eyebrow">FİLTRELER</div><h2>{filtered.length} kayıt</h2></div>
            <div className="filter-row">
              <div className="chip" style={{display:"flex",gap:6,alignItems:"center"}}>
                <Search size={12}/>
                <input placeholder="Kullanıcı / hedef ara" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search" style={{background:"transparent",border:0,color:"inherit",outline:"none",font:"inherit",width:160}}/>
              </div>
              <div className="chip" style={{display:"flex",gap:6,alignItems:"center"}}>
                <Filter size={12}/>
                <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} data-testid="audit-action-filter" style={{background:"transparent",border:0,color:"inherit",outline:"none",font:"inherit"}}>
                  <option value="">Tüm aksiyonlar</option>
                  {actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="audit-list" data-testid="audit-list">
            {filtered.map((r) => (
              <article key={r.id} className="audit-row" data-testid={`audit-${r.id}`}>
                <span className="audit-time">{(r.created_at || "").slice(0,19).replace("T"," ")}</span>
                <span className={`audit-action a-${r.action.split(".")[0]}`}>{ACTION_LABELS[r.action] || r.action}</span>
                <span className="audit-user">{r.user_name} <small>· {r.role}</small></span>
                <span className="audit-target">{r.target_type} <code>{r.target_id}</code></span>
                <span className="audit-meta">{r.assessment_id ? `değerlendirme ${r.assessment_id}` : ""}</span>
              </article>
            ))}
            {!filtered.length && <small className="evidence-empty">Seçili filtrede kayıt yok.</small>}
          </div>
          <small className="legal-foot"><FileText size={13}/> Her kayıt, kaynak maddesi ve kullanıcı ile birlikte saklanır. Veri değiştirilemez.</small>
        </section>
      </div>
    </div>
  );
}
